














import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { Chart } from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import { colors } from '@/config';
import KeyValue from '@/utils/KeyValue';
import {
  CHART_WIDTH_BASELINE,
  CompassSlideLayout,
  applyMultiSeriesCompassLayout,
  applyPolarCompassChartSize,
  scaleChartPx,
} from '@/utils/chartPresentationSizing';

More(Highcharts);

@Component({ components: { Chart } })
export default class CompassIndustryCqw extends Vue {
  @Prop() readonly rankings!: any;
  @Prop() containerWidth: any;
  @Prop({ default: true }) readonly dark!: boolean;
  @Prop() readonly branch!: string;
  @Prop({ default: 'auto' }) readonly slideLayout!: CompassSlideLayout;

  component: any = undefined;
  hovered: number | null = null;

  chartOptions: any = {
    chart: {
      polar: true,
      type: 'area',
      backgroundColor: 'transparent',
      events: {
        click: this.onClick,
      },
    },

    credits: {
      enabled: false,
    },

    title: {
      text: '',
    },

    pane: {
      size: '80%',
    },

    xAxis: {
      // tslint:disable-next-line:max-line-length
      categories: [
        '<span style="font-weight: 600; color: white;">' +
          Vue.prototype.$t('Avkastning') +
          '</span>',
        Vue.prototype.$t('Rörelsemarginal'),
        Vue.prototype.$t('Tillväxt'),
        '<span  style="font-weight: 600; color: white;">' +
          Vue.prototype.$t('Framtidsutsikter') +
          '</span>',
        Vue.prototype.$t('Soliditet'),
        Vue.prototype.$t('Rörelsekapital i % av omsättning'),
        '<span style="font-weight: 600; color: white;">' +
          Vue.prototype.$t('Finansiell styrka') +
          '</span>',
        Vue.prototype.$t('Kassalikviditet'),
        Vue.prototype.$t('Räntetäckningsgrad'),
        '<span style="font-weight: 600; color: white;">' +
          Vue.prototype.$t('Kortsiktig beredskap') +
          '</span>',
        Vue.prototype.$t('Riskbuffert'),
        Vue.prototype.$t('Räntabilitet på eget kapital'),
      ],
      tickmarkPlacement: 'on',
      lineWidth: 0,
      labels: {
        enabled: true,
        style: {
          fontSize: '1cqw',
          color: colors.LightGrey,
        },
        distance: this.transformPx(25),
      },
      plotLines: [
        {
          color: colors.Black,
          width: this.transformPx(4),
          value: 3,
          zIndex: 2,
        },
        {
          color: colors.Black,
          width: this.transformPx(4),
          value: 6,
          zIndex: 2,
        },
        {
          color: colors.Black,
          width: this.transformPx(4),
          value: 9,
          zIndex: 2,
        },
        {
          color: colors.Black,
          width: this.transformPx(4),
          value: 12,
          zIndex: 2,
        },
      ],
      gridLineColor: colors.Black,
    },

    yAxis: {
      gridLineInterpolation: 'polygon',
      lineWidth: 0,
      min: 0,
      max: 100,
      tickInterval: 10,
      gridLineColor: colors.Black,
      gridLineDashStyle: 'longdash',
      labels: {
        enabled: false,
      },
    },

    tooltip: {
      shared: true,
      useHTML: true,
      headerFormat: '<table>',
      pointFormat:
        '<tr><td style="font-size: 1.25cqw;">{series.name}</td><td style="text-align: right; font-size: 1.25cqw; padding-left: 10px"><b>{point.y}</b></td></tr>',
      backgroundColor: this.dark ? colors.Dark : colors.LightGrey,
      style: {
        color: this.dark ? colors.LightGrey : colors.Black,
      },
    },

    legend: {
      reversed: false,
      enabled: true,
      floating: true,
      borderWidth: this.transformPx(1),
      padding: this.transformPx(16),
      align: 'left',
      verticalAlign: 'top',
      x: 0,
      y: 0,
      borderColor: 'transparent',
      layout: 'vertical',
      itemStyle: {
        font: 'Assistant, Verdana, sans-serif',
        fontSize: '1cqw',
        fontWeight: 'normal',
        color: colors.LightGrey,
      },
      itemHoverStyle: {
        color: colors.White,
      },
    },

    plotOptions: {
      series: {
        animation: true,
        fillOpacity: 0.5,
        marker: {
          radius: 0,
          states: {
            hover: {
              radius: this.transformPx(8),
            },
          },
          symbol: 'circle',
        },
        lineWidth: this.transformPx(1),
        pointPlacement: 'on',
        point: {
          events: {
            mouseOver: this.onMouseOver,
            click: this.onClick,
          },
        },
        events: {
          mouseOut: this.onMouseOut,
        },
        showInLegend: true,
      },
    },

    series: [],
  };

  onMouseOver(e: any) {
    this.hovered = e.target.index;
    this.component.$emit('hover', KeyValue.ByIndex(this.hovered as number));
  }

  onMouseOut() {
    this.hovered = null;
    this.component.$emit('hover', null);
  }

  onClick(e: any) {
    if (!this.hovered || this.hovered % 3 === 0) {
      return;
    }
    this.component.$emit('clicked');
  }

  @Watch('rankings', { immediate: true, deep: true })
  onRankingsChanged() {
    if (this.rankings === undefined) {
      return;
    }
    if (this.rankings.sheets === undefined) {
      return;
    }

    const sheet = this.rankings.sheets[0];

    // Lägg till grundstruktur-serie som alltid visas
    const baseStructure = {
      name: 'Grundstruktur',
      showInLegend: false,
      enableMouseTracking: false,
      data: Array(12).fill(0),
      color: 'transparent',
      zIndex: 0,
      visible: true,
    };

    const s1 = this.addData(
      {
        name: `${sheet.DATE_TO.substring(0, 7)} (${
          sheet.MONTHS
        } ${Vue.prototype.$t('mån')})`,
        pointPlacement: 'on',
        fillOpacity: 0.5,
        color: colors.Secondary,
        zIndex: 6,
        lineWidth: this.transformPx(3),
        showInLegend: true,
        enableMouseTracking: true,
        visible: true,
      },
      sheet.ranks,
      sheet.keyValues,
    );

    const industry = this.addData(
      {
        name: Vue.prototype.$t('Branschmedian'),
        pointPlacement: 'on',
        fillOpacity: 0,
        color: colors.Tertiary,
        dashStyle: 'longdash',
        zIndex: 5,
        lineWidth: this.transformPx(3),
        showInLegend: true,
        enableMouseTracking: true,
        visible: true,
      },
      sheet.industryRanks,
      sheet.industryKeyValues,
    );

    const industry25 = this.addData(
      {
        name: Vue.prototype.$t('Undre kvartil'),
        pointPlacement: 'on',
        fillOpacity: 0,
        color: colors.Negative,
        lineColor: colors.Negative,
        zIndex: 5,
        lineWidth: this.transformPx(3),
        showInLegend: true,
        enableMouseTracking: true,
        visible: true,
      },
      sheet.industryRanks25,
      sheet.industryKeyValues25,
    );

    const industry75 = this.addData(
      {
        name: Vue.prototype.$t('Övre kvartil'),
        pointPlacement: 'on',
        fillOpacity: 0,
        color: colors.Positive,
        lineColor: colors.Positive,
        zIndex: 5,
        lineWidth: this.transformPx(3),
        showInLegend: true,
        enableMouseTracking: true,
        visible: true,
      },
      sheet.industryRanks75,
      sheet.industryKeyValues75,
    );

    this.chartOptions.series = [
      baseStructure,
      s1,
      industry25,
      industry,
      industry75,
    ];
    const w =
      this.containerWidth > 0 ? this.containerWidth : CHART_WIDTH_BASELINE;
    applyMultiSeriesCompassLayout(this.chartOptions, w, this.slideLayout);
    this.reflowChart();
  }

  addData(series: any, ranks: any, kv: any) {
    series.data = KeyValue.Items.map((item) => ({
      name: item.Name(),
      y: item.Value(ranks),
      keyfigure: item.KeyFigure(kv),
    }));
    return series;
  }

  @Watch('containerWidth', { immediate: true })
  syncChartSpacing() {
    const w =
      this.containerWidth > 0 ? this.containerWidth : CHART_WIDTH_BASELINE;
    applyMultiSeriesCompassLayout(this.chartOptions, w, this.slideLayout);
    this.reflowChart();
  }

  @Watch('slideLayout')
  onSlideLayoutChanged() {
    this.syncChartSpacing();
  }

  reflowChart() {
    this.$nextTick(() => {
      const slot = this.$refs.chartSlot as HTMLElement;
      const chart = this.$refs.chart as any;
      applyPolarCompassChartSize(
        chart && chart.chart,
        this.chartOptions,
        slot,
        this.slideLayout,
      );
    });
  }

  mounted() {
    this.reflowChart();
  }

  created() {
    this.component = this;
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  }

  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    // this.chartOptions.chart.spacingLeft = this.vw(20);
  }

  vw(vw: number): number {
    return (window.innerWidth * vw) / 100.0;
  }

  get filters(): any {
    return (Vue as any).options.filters;
  }

  get cursor() {
    if (!this.hovered || this.hovered % 3 === 0) {
      return 'default';
    } else {
      return 'pointer';
    }
  }

  transformPx(value: any) {
    const width =
      this.containerWidth > 0 ? this.containerWidth : CHART_WIDTH_BASELINE;
    if (value * (width / CHART_WIDTH_BASELINE) < 1) {
      return 1;
    }
    return value * (width / CHART_WIDTH_BASELINE);
  }
}
